import React, { useState, useEffect, useRef, useCallback } from "react";
import MainLayout from "../../components/layout/MainLayout";
import PageLoader from "../../components/common/PageLoader";
import api from "../../services/api";
import Swal from "sweetalert2";
import "./AttendanceSettings.css";

// Leaflet imports - loaded dynamically to avoid SSR issues
let L = null;

function AttendanceSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);

  // Current saved settings for display
  const [savedData, setSavedData] = useState(null);

  const [formData, setFormData] = useState({
    clock_in: "",
    clock_out: "",
    grace_time: "15",
    overtime_rate: "2",
    latitude: "0",
    longitude: "0",
    geometers: "100",
  });

  const [errors, setErrors] = useState({});

  // Map refs
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await api.get("/attendance-settings");
      if (response.data.success && response.data.data) {
        const d = response.data.data;
        setSavedData(d);
        setFormData({
          clock_in: d.clock_in || "",
          clock_out: d.clock_out || "",
          grace_time: d.grace_time || "15",
          overtime_rate: d.overtime_rate || "2",
          latitude: d.latitude || "0",
          longitude: d.longitude || "0",
          geometers: d.geometers || "100",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load attendance settings",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Auto-calculate hours worked from clock in/out
  const calcHoursWorked = () => {
    if (!formData.clock_in || !formData.clock_out) return "N/A";
    try {
      const [inH, inM] = formData.clock_in.split(":").map(Number);
      const [outH, outM] = formData.clock_out.split(":").map(Number);
      const totalIn = inH * 60 + inM;
      const totalOut = outH * 60 + outM;
      const diff = totalOut - totalIn;
      if (diff <= 0) return "Invalid";
      const hrs = Math.floor(diff / 60);
      const mins = diff % 60;
      return `${String(hrs).padStart(2, "0")}:${String(mins).padStart(2, "0")} hrs`;
    } catch {
      return "N/A";
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.clock_in) newErrors.clock_in = "Clock in time is required";
    if (!formData.clock_out) newErrors.clock_out = "Clock out time is required";
    if (
      formData.clock_in &&
      formData.clock_out &&
      formData.clock_in >= formData.clock_out
    ) {
      newErrors.clock_out = "Clock out must be after clock in";
    }
    if (!formData.grace_time && formData.grace_time !== 0)
      newErrors.grace_time = "Grace time is required";
    if (!formData.overtime_rate && formData.overtime_rate !== 0)
      newErrors.overtime_rate = "Overtime rate is required";
    if (!formData.latitude) newErrors.latitude = "Latitude is required";
    if (!formData.longitude) newErrors.longitude = "Longitude is required";
    if (!formData.geometers) newErrors.geometers = "Geometers is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      const response = await api.post("/attendance-settings", formData);
      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Saved!",
          text: response.data.message,
          timer: 2000,
          showConfirmButton: false,
        });
        // Refresh saved display
        setSavedData({
          ...formData,
          hours_worked: response.data.hours_worked,
        });
      }
    } catch (error) {
      const serverErrors = error.response?.data?.errors || {};
      if (Object.keys(serverErrors).length > 0) {
        const mapped = {};
        Object.keys(serverErrors).forEach((k) => {
          mapped[k] = serverErrors[k][0];
        });
        setErrors(mapped);
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.response?.data?.message || "Failed to save settings",
        });
      }
    } finally {
      setSaving(false);
    }
  };

  // ─── Map Logic ────────────────────────────────────────────────────────────

  const initMap = useCallback(async () => {
    if (!mapRef.current) return;
    if (mapInstanceRef.current) return; // already initialized

    // Dynamically load Leaflet
    if (!L) {
      L = (await import("leaflet")).default;
      await import("leaflet/dist/leaflet.css");

      // Fix Leaflet default marker icons
      delete L.Icon.Default.prototype._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl:
          "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
        iconUrl:
          "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
        shadowUrl:
          "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
      });
    }

    const lat = parseFloat(formData.latitude) || 20.5937;
    const lng = parseFloat(formData.longitude) || 78.9629;

    const map = L.map(mapRef.current).setView([lat, lng], 13);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap contributors",
    }).addTo(map);

    // Add initial marker
    const marker = L.marker([lat, lng], { draggable: true }).addTo(map);
    marker.bindPopup("📍 Office Location").openPopup();

    // Update coords on drag
    marker.on("dragend", function (e) {
      const pos = e.target.getLatLng();
      setFormData((prev) => ({
        ...prev,
        latitude: pos.lat.toFixed(6),
        longitude: pos.lng.toFixed(6),
      }));
    });

    // Move marker on map click
    map.on("click", function (e) {
      marker.setLatLng(e.latlng);
      setFormData((prev) => ({
        ...prev,
        latitude: e.latlng.lat.toFixed(6),
        longitude: e.latlng.lng.toFixed(6),
      }));
    });

    mapInstanceRef.current = map;
    markerRef.current = marker;
  }, [formData.latitude, formData.longitude]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Initialize map when modal opens
  useEffect(() => {
    if (showMapModal) {
      setTimeout(() => initMap(), 300);
    } else {
      // Cleanup map on close
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        markerRef.current = null;
      }
    }
  }, [showMapModal, initMap]);

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      Swal.fire({
        icon: "error",
        title: "Not Supported",
        text: "Geolocation is not supported by this browser.",
      });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setFormData((prev) => ({
          ...prev,
          latitude: latitude.toFixed(6),
          longitude: longitude.toFixed(6),
        }));
        if (mapInstanceRef.current && markerRef.current) {
          const latlng = [latitude, longitude];
          mapInstanceRef.current.setView(latlng, 15);
          markerRef.current.setLatLng(latlng);
        }
      },
      () => {
        Swal.fire({
          icon: "error",
          title: "Location Error",
          text: "Unable to get your location. Please allow location access.",
        });
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const handleSearchLocation = async () => {
    if (!searchQuery.trim()) return;
    setSearchLoading(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(searchQuery)}&format=json&limit=1`,
      );
      const results = await res.json();
      if (results.length > 0) {
        const { lat, lon } = results[0];
        const latitude = parseFloat(lat).toFixed(6);
        const longitude = parseFloat(lon).toFixed(6);
        setFormData((prev) => ({ ...prev, latitude, longitude }));
        if (mapInstanceRef.current && markerRef.current) {
          const latlng = [parseFloat(lat), parseFloat(lon)];
          mapInstanceRef.current.setView(latlng, 15);
          markerRef.current.setLatLng(latlng);
        }
      } else {
        Swal.fire({
          icon: "warning",
          title: "Not Found",
          text: "Location not found. Try a different search term.",
        });
      }
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to search location.",
      });
    } finally {
      setSearchLoading(false);
    }
  };

  const handleConfirmLocation = () => {
    setShowMapModal(false);
    if (errors.latitude)
      setErrors((prev) => ({ ...prev, latitude: "", longitude: "" }));
  };

  if (loading) {
    return <MainLayout><PageLoader force copy="Loading settings..." /></MainLayout>;
  }

  const formatTime = (t) => {
    if (!t) return "N/A";
    try {
      const [h, m] = t.split(":");
      const date = new Date();
      date.setHours(parseInt(h), parseInt(m));
      return date.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return t;
    }
  };

  return (
    <MainLayout>
      <div className="att-settings-container">
        <div className="att-settings-grid">
          {/* ── Left: Current Settings Summary ── */}
          <div className="att-summary-card">
            <div className="att-card-header-strip">
              <i className="fas fa-clock"></i>
              <h5>Current Settings</h5>
            </div>
            <div className="att-summary-body">
              {!savedData ? (
                <div className="no-settings-msg">
                  <i className="fas fa-cog"></i>
                  <p>
                    No settings configured yet. Fill the form to get started.
                  </p>
                </div>
              ) : (
                <>
                  <SummaryRow
                    icon="fa-sign-in-alt"
                    label="Clock In"
                    value={formatTime(savedData.clock_in)}
                    color="#6557ff"
                  />
                  <SummaryRow
                    icon="fa-sign-out-alt"
                    label="Clock Out"
                    value={formatTime(savedData.clock_out)}
                    color="#10b981"
                  />
                  <SummaryRow
                    icon="fa-hourglass-half"
                    label="Work Hours"
                    value={
                      savedData.hours_worked
                        ? `${savedData.hours_worked} hrs`
                        : "N/A"
                    }
                    color="#3b82f6"
                  />
                  <SummaryRow
                    icon="fa-stopwatch"
                    label="Grace Time"
                    value={
                      savedData.grace_time
                        ? `${savedData.grace_time} minutes`
                        : "N/A"
                    }
                    color="#f59e0b"
                  />
                  <SummaryRow
                    icon="fa-bolt"
                    label="Overtime Rate"
                    value={
                      savedData.overtime_rate
                        ? `${savedData.overtime_rate}x`
                        : "N/A"
                    }
                    color="#ef4444"
                  />
                  <SummaryRow
                    icon="fa-map-marker-alt"
                    label="Office Location"
                    value={
                      savedData.latitude && savedData.longitude
                        ? `${parseFloat(savedData.latitude).toFixed(4)}, ${parseFloat(savedData.longitude).toFixed(4)}`
                        : "N/A"
                    }
                    color="#ec4899"
                  />
                </>
              )}
            </div>
          </div>

          {/* ── Right: Edit Form ── */}
          <div className="att-form-card">
            <div className="att-card-header-strip">
              <i className="fas fa-sliders-h"></i>
              <h5>Configure Settings</h5>
            </div>

            <form onSubmit={handleSubmit} className="att-form-body">
              {/* Clock In / Clock Out */}
              <div className="att-form-row-2col">
                <div className="att-form-group">
                  <label className="att-label">
                    Clock In Time <span className="required">*</span>
                  </label>
                  <div
                    className={`att-input-box ${errors.clock_in ? "error" : ""}`}
                  >
                    <i className="fas fa-sign-in-alt att-input-icon"></i>
                    <input
                      type="time"
                      name="clock_in"
                      value={formData.clock_in}
                      onChange={handleInputChange}
                    />
                  </div>
                  {errors.clock_in && (
                    <span className="att-error">{errors.clock_in}</span>
                  )}
                </div>

                <div className="att-form-group">
                  <label className="att-label">
                    Clock Out Time <span className="required">*</span>
                  </label>
                  <div
                    className={`att-input-box ${errors.clock_out ? "error" : ""}`}
                  >
                    <i className="fas fa-sign-out-alt att-input-icon"></i>
                    <input
                      type="time"
                      name="clock_out"
                      value={formData.clock_out}
                      onChange={handleInputChange}
                    />
                  </div>
                  {errors.clock_out && (
                    <span className="att-error">{errors.clock_out}</span>
                  )}
                </div>
              </div>

              {/* Hours preview */}
              {formData.clock_in && formData.clock_out && (
                <div className="att-hours-preview">
                  <i className="fas fa-calculator"></i>
                  <span>
                    Calculated Work Hours: <strong>{calcHoursWorked()}</strong>
                  </span>
                </div>
              )}

              {/* Grace Time / Overtime Rate */}
              <div className="att-form-row-2col">
                <div className="att-form-group">
                  <label className="att-label">
                    Grace Time (minutes) <span className="required">*</span>
                  </label>
                  <div
                    className={`att-input-box ${errors.grace_time ? "error" : ""}`}
                  >
                    <i className="fas fa-stopwatch att-input-icon"></i>
                    <input
                      type="number"
                      name="grace_time"
                      value={formData.grace_time}
                      onChange={handleInputChange}
                      min="0"
                      placeholder="e.g. 15"
                    />
                    <span className="att-input-suffix">min</span>
                  </div>
                  {errors.grace_time && (
                    <span className="att-error">{errors.grace_time}</span>
                  )}
                </div>

                <div className="att-form-group">
                  <label className="att-label">
                    Overtime Rate (multiplier){" "}
                    <span className="required">*</span>
                  </label>
                  <div
                    className={`att-input-box ${errors.overtime_rate ? "error" : ""}`}
                  >
                    <i className="fas fa-bolt att-input-icon"></i>
                    <input
                      type="number"
                      name="overtime_rate"
                      value={formData.overtime_rate}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      placeholder="e.g. 2"
                    />
                    <span className="att-input-suffix">x</span>
                  </div>
                  {errors.overtime_rate && (
                    <span className="att-error">{errors.overtime_rate}</span>
                  )}
                </div>
              </div>

              {/* Latitude / Longitude */}
              <div className="att-form-row-2col">
                <div className="att-form-group">
                  <label className="att-label">
                    Latitude <span className="required">*</span>
                  </label>
                  <div
                    className={`att-input-box ${errors.latitude ? "error" : ""}`}
                  >
                    <i className="fas fa-map-pin att-input-icon"></i>
                    <input
                      type="number"
                      name="latitude"
                      value={formData.latitude}
                      onChange={handleInputChange}
                      step="0.000001"
                      placeholder="e.g. 28.613939"
                    />
                    <button
                      type="button"
                      className="att-map-btn"
                      onClick={() => setShowMapModal(true)}
                      title="Pick on map"
                    >
                      <i className="fas fa-map-marker-alt"></i>
                    </button>
                  </div>
                  {errors.latitude && (
                    <span className="att-error">{errors.latitude}</span>
                  )}
                </div>

                <div className="att-form-group">
                  <label className="att-label">
                    Longitude <span className="required">*</span>
                  </label>
                  <div
                    className={`att-input-box ${errors.longitude ? "error" : ""}`}
                  >
                    <i className="fas fa-map-pin att-input-icon"></i>
                    <input
                      type="number"
                      name="longitude"
                      value={formData.longitude}
                      onChange={handleInputChange}
                      step="0.000001"
                      placeholder="e.g. 77.208490"
                    />
                    <button
                      type="button"
                      className="att-map-btn"
                      onClick={() => setShowMapModal(true)}
                      title="Pick on map"
                    >
                      <i className="fas fa-map-marker-alt"></i>
                    </button>
                  </div>
                  {errors.longitude && (
                    <span className="att-error">{errors.longitude}</span>
                  )}
                </div>

                <div className="att-form-group">
                  <label className="att-label">
                    Geofence Radius <span className="required">*</span>
                  </label>
                  <div
                    className={`att-input-box ${errors.geometers ? "error" : ""}`}
                  >
                    <i className="fa-regular fa-circle-dot att-input-icon"></i>
                    <input
                      type="number"
                      name="geometers"
                      value={formData.geometers}
                      onChange={handleInputChange}
                      step="0.000001"
                      placeholder="100 (e.g. 100 meters)"
                    />
                  </div>
                  {errors.geometers && (
                    <span className="att-error">{errors.geometers}</span>
                  )}
                </div>
              </div>

              {/* Coordinates preview */}
              {formData.latitude &&
                formData.longitude &&
                formData.latitude !== "0" &&
                formData.longitude !== "0" && (
                  <div className="att-coords-preview">
                    <i className="fas fa-crosshairs"></i>
                    <span>
                      Selected:{" "}
                      <strong>
                        {parseFloat(formData.latitude).toFixed(6)}
                      </strong>
                      ,{" "}
                      <strong>
                        {parseFloat(formData.longitude).toFixed(6)}
                      </strong>
                    </span>
                    <button
                      type="button"
                      className="view-map-link"
                      onClick={() => setShowMapModal(true)}
                    >
                      View on map
                    </button>
                  </div>
                )}

              {/* Submit */}
              <div className="att-form-footer">
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="btn-spinner"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i>
                      Save Settings
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* ── Map Modal ── */}
        {showMapModal && (
          <>
            <div
              className="modal-backdrop-custom"
              onClick={() => setShowMapModal(false)}
            ></div>
            <div className="map-modal">
              <div className="map-modal-header">
                <h5>
                  <i className="fas fa-map-marked-alt"></i>
                  Pick Office Location
                </h5>
                <button
                  className="close-modal-btn"
                  onClick={() => setShowMapModal(false)}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div className="map-modal-toolbar">
                <div className="map-search-box">
                  <i className="fas fa-search"></i>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && handleSearchLocation()
                    }
                    placeholder="Search location (e.g. Connaught Place, Delhi)"
                  />
                  <button
                    type="button"
                    onClick={handleSearchLocation}
                    disabled={searchLoading}
                  >
                    {searchLoading ? (
                      <span className="btn-spinner dark"></span>
                    ) : (
                      "Search"
                    )}
                  </button>
                </div>
                <button
                  type="button"
                  className="btn-my-location"
                  onClick={handleUseMyLocation}
                >
                  <i className="fas fa-location-arrow"></i>
                  Use My Location
                </button>
              </div>

              <div className="map-tip">
                <i className="fas fa-info-circle"></i>
                Click on the map or drag the marker to set the office location.
              </div>

              {/* Map container */}
              <div ref={mapRef} className="map-container"></div>

              <div className="map-modal-footer">
                <div className="map-coords-display">
                  <span>
                    Lat:{" "}
                    <strong>
                      {parseFloat(formData.latitude || 0).toFixed(6)}
                    </strong>
                  </span>
                  <span>
                    Lng:{" "}
                    <strong>
                      {parseFloat(formData.longitude || 0).toFixed(6)}
                    </strong>
                  </span>
                </div>
                <div className="map-footer-actions">
                  <button
                    type="button"
                    className="btn-map-cancel"
                    onClick={() => setShowMapModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn-map-confirm"
                    onClick={handleConfirmLocation}
                  >
                    <i className="fas fa-check"></i>
                    Confirm Location
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
}

// Helper sub-component
function SummaryRow({ icon, label, value, color }) {
  return (
    <div className="summary-row">
      <div className="summary-icon" style={{ background: `${color}18`, color }}>
        <i className={`fas ${icon}`}></i>
      </div>
      <div className="summary-text">
        <span className="summary-label">{label}</span>
        <span className="summary-value">{value}</span>
      </div>
    </div>
  );
}

export default AttendanceSettings;
